import { useQuery } from "react-query";
import { useAuth } from "../../hooks/useAuth";
import Select from "react-select";
import { backendApis } from "../../config";
import { useEffect, useMemo, useRef, useState } from "react";
import { Form } from "react-bootstrap";

const SELECT_ALL = { value: "*", label: "Select all" };

export default function UserToBranchPicker({
  selectedRootBranches = [],
  selectedBranches = {},
  setSelectedBranches,
  isEdit = false,
  staffId = "",
}) {
  const { user: authUser, backendUrl } = useAuth();
  const hasPrePopulated = useRef(false);

  const branches = useMemo(() => {
    return backendApis.map((el) => ({
      ...el,
      label: el.name,
      value: el.name,
    }));
  }, [authUser]);

  const fetchSubBranches = async () => {
    const responses = await Promise.all(
      branches.map((el) => fetch(`${backendUrl}/api/branch`)),
    );
    const data = await Promise.all(responses.map((res) => res.json()));

    return data.map((el, index) => ({
      branches: el.data.branches.map((branch) => ({
        label: branch.name,
        value: branch.id,
      })),
      ...branches.find((_, i) => i === index),
    }));
  };

  const { data: branchesWithSubBranches } = useQuery(
    ["branchesWithSubBranches", authUser?.companyId],
    () => fetchSubBranches(authUser?.companyId),
  );

  const { data: staffAssignedBranches } = useQuery(
    ["staffBranches", staffId],
    async () => {
      const responses = await Promise.all(
        branches.map((el) =>
          fetch(
            `${el.url}/api/branch/get-branches-by-staff?Staff_ID=${staffId}`,
            {
              method: "GET",
              headers: {
                Accept: "Application/json",
                "Content-Type": "Application/json",
              },
              credentials: "include",
            },
          ),
        ),
      );
      const data = await Promise.all(responses.map((res) => res.json()));
      return data.map((d, i) => ({
        companyValue: branches[i].value,
        assignedBranches: (d?.data?.branches || []).map((branch) => ({
          label: branch.name,
          value: branch.id,
        })),
      }));
    },
    { enabled: isEdit && !!staffId },
  );

  useEffect(() => {
    if (
      !isEdit ||
      !staffId ||
      !staffAssignedBranches ||
      !branchesWithSubBranches ||
      hasPrePopulated.current
    )
      return;

    const prepopulated = {};
    staffAssignedBranches.forEach(({ companyValue, assignedBranches }) => {
      prepopulated[companyValue] = assignedBranches;
    });

    if (Object.keys(prepopulated).length > 0) {
      setSelectedBranches(prepopulated);
      hasPrePopulated.current = true;
    }
  }, [isEdit, staffId, staffAssignedBranches, branchesWithSubBranches]);

  const isSelectedInRoot = (branch) => {
    return selectedRootBranches
      ? selectedRootBranches.some((selected) => selected.value === branch.value)
      : false;
  };

  const handleSelectedBranches = ({ parentBranchId, selected, allOptions }) => {
    if (selected?.some((o) => o.value === "*")) {
      // if all were already selected, clicking "Select all" clears; otherwise fill all
      const currentlyAll =
        (selectedBranches[parentBranchId] || []).length === allOptions.length;
      setSelectedBranches({
        ...selectedBranches,
        [parentBranchId]: currentlyAll ? [] : allOptions,
      });
    } else {
      setSelectedBranches({
        ...selectedBranches,
        [parentBranchId]: selected,
      });
    }
  };

  const formatOptionLabel = (opt, parentBranchId, allOptions) => {
    if (opt.value !== "*") return opt.label;

    const selectedCount = (selectedBranches[parentBranchId] || []).length;
    const allSelected = selectedCount === allOptions.length;
    const someSelected = selectedCount > 0 && !allSelected;

    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          fontWeight: 500,
        }}
      >
        <span
          style={{
            width: 15,
            height: 15,
            border: "1.5px solid #378ADD",
            borderRadius: 3,
            background: allSelected ? "#378ADD" : "transparent",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            fontSize: 11,
            color: "#fff",
          }}
        >
          {allSelected ? "✓" : someSelected ? "" : ""}
        </span>
        Select all
      </div>
    );
  };

  return branchesWithSubBranches ? (
    <div>
      {branchesWithSubBranches.map((el) => {
        const allOptions = el.branches;
        const optionsWithSelectAll = [SELECT_ALL, ...allOptions];

        return (
          <div
            key={el.value}
            style={{ display: isSelectedInRoot(el) ? "block" : "none" }}
            className="mb-3 ps-5"
          >
            <Form.Label>{el.label}</Form.Label>

            <Select
              options={optionsWithSelectAll}
              value={selectedBranches[el.value] || []}
              onChange={(selected) =>
                handleSelectedBranches({
                  parentBranchId: el.value,
                  selected,
                  allOptions,
                })
              }
              isMulti
              placeholder={`Select sub-branches for ${el.label}`}
              formatOptionLabel={(opt) =>
                formatOptionLabel(opt, el.value, allOptions)
              }
              filterOption={(opt, input) =>
                opt.value === "*"
                  ? !input // hide Select All when searching
                  : opt.label.toLowerCase().includes(input.toLowerCase())
              }
              menuPlacement="top"
            />
          </div>
        );
      })}
    </div>
  ) : null;
}

export function BranchInvoicePicker({ formik, fieldName = "targetBranch" }) {
  const { backendUrl } = useAuth();
  const [selectedCompany, setSelectedCompany] = useState(null);

  // Companies come from backendApis config (same as above)
  const companies = useMemo(() => {
    return backendApis.map((el) => ({
      ...el,
      label: el.name,
      value: el.name,
    }));
  }, []);

  // Fetch branches for selected company only
  const fetchBranchesForCompany = async (company) => {
    const response = await fetch(`${company.url}/api/branch`, {
      method: "GET",
      headers: {
        Accept: "Application/json",
        "Content-Type": "Application/json",
      },
      credentials: "include",
    });
    const data = await response.json();
    return (data?.data?.branches || []).map((branch) => ({
      label: branch.name,
      value: branch.id,
    }));
  };

  const {
    data: branchOptions = [],
    isFetching: isFetchingBranches,
    refetch: refetchBranches,
  } = useQuery(
    ["branchesForCompany", selectedCompany?.value],
    () => fetchBranchesForCompany(selectedCompany),
    {
      enabled: !!selectedCompany, // only fetch when a company is selected
      keepPreviousData: false,
    },
  );

  const handleCompanyChange = (selected) => {
    setSelectedCompany(selected);
    // clear branch selection when company changes
    formik.setFieldValue(fieldName, null);
  };

  const handleBranchChange = (selected) => {
    formik.setFieldValue(fieldName, {
      ...selected,
      companyValue: selectedCompany.value,
      companyUrl: selectedCompany.url,
    });
  };

  return (
    <div>
      <Form.Group className="mb-3">
        <Form.Label>Company</Form.Label>
        <Select
          classNamePrefix="form-select"
          placeholder="Select company"
          options={companies}
          value={selectedCompany}
          onChange={handleCompanyChange}
          menuPlacement="auto"
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Branch</Form.Label>
        <Select
          classNamePrefix="form-select"
          placeholder={
            !selectedCompany
              ? "Select a company first"
              : isFetchingBranches
              ? "Loading branches..."
              : "Select branch"
          }
          options={branchOptions}
          value={formik.values[fieldName] || null}
          onChange={handleBranchChange}
          isDisabled={!selectedCompany || isFetchingBranches}
          isLoading={isFetchingBranches}
          menuPlacement="auto"
          noOptionsMessage={() =>
            !selectedCompany ? "Select a company first" : "No branches found"
          }
        />
        {formik.touched[fieldName] && formik.errors[fieldName] && (
          <Form.Text className="text-danger">
            {formik.errors[fieldName]}
          </Form.Text>
        )}
      </Form.Group>
    </div>
  );
}
